import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import queryString from 'query-string'
import { actions as historyActions } from 'src/features/fw-ai/children/history/HistorySlice'
import { actions as chatActions } from 'src/features/fw-ai/children/dougallChat/ChatSlice'
import { actions as chatPageActions } from 'src/features/fw-ai/children/chatPage/ChatPageSlice'
import useChat from './useChat'

function decodeHtml(html) {
  let txt = document.createElement('textarea')
  txt.innerHTML = html
  const textValue = txt.value
  txt.remove()
  return textValue
}

function safeDecodeURIComponent(str) {
  if (typeof str !== 'string') return str
  try {
    return decodeURIComponent(str)
  } catch {
    return str
  }
}

export const prepromptRef = { current: null }

/**
 * Hook to handle all URL related prompts
 */
const useUrlPrompts = () => {
  const dispatch = useDispatch()
  const { isFetching, promptRequest, setPromptNum } = useChat()
  const { t } = useTranslation()

  const {
    source = undefined,
    prompt = undefined,
    promptNum = undefined,
    dspId = undefined,
    pId = undefined,
    articleId = undefined,
    type = undefined,
    preprompt: prepromptFromUrl = undefined
  } = queryString.parse(window.location.search)
  /**
   * URL prompt params action
   */
  useEffect(() => {
    // Prompt from UBQ3 ads
    // expects url params: dspId, prompt, promptNum
    if (dspId && prompt && promptNum && !isFetching) {
      promptRequest({
        hcpPrompt: decodeHtml(prompt),
        articleId,
        type: 'ubq3'
      })
      setPromptNum(promptNum)
    } else if (articleId && type) {
      let dougallAppPrompt
      switch (type) {
        case 'implications':
          dougallAppPrompt = t('dougallAppsPromptImplications')
          break
        case 'explain':
          dougallAppPrompt = t('dougallAppsPromptExplain')
          break
        default:
          dougallAppPrompt = decodeHtml(prompt)
          break
      }
      promptRequest({
        hcpPrompt: dougallAppPrompt,
        articleId,
        type
      })
    } else if (prompt && source && !isFetching) {
      promptRequest({
        hcpPrompt: decodeHtml(prompt),
        articleId,
        type: source === 'DGPTLessons' ? 'lessons' : 'regular'
      })
    }
  }, [prompt, promptNum])

  useEffect(() => {
    if (pId && source) {
      dispatch(historyActions.requestPromptHistory({ pId, source }))
    }
  }, [pId, source])

  useEffect(() => {
    const rawFromUrl =
      typeof prepromptFromUrl === 'string' ? prepromptFromUrl : null
    const rawFromStorage =
      !rawFromUrl && typeof window !== 'undefined'
        ? window.localStorage.getItem('fwai:preprompt')
        : null

    const chosenRaw = rawFromUrl || rawFromStorage
    if (!chosenRaw) return

    const decoded = safeDecodeURIComponent(chosenRaw)

    prepromptRef.current = decoded
    dispatch(chatActions.setFormState({ prompt: decoded }))
    dispatch(chatPageActions.displayContainer({ container: 'chat' }))

    if (rawFromStorage) {
      try {
        window.localStorage.removeItem('fwai:preprompt')
      } catch {}
    }

    if (rawFromUrl) {
      const search = window.location.search || ''
      const params = queryString.parse(search)
      delete params.preprompt
      const cleaned = queryString.stringify(params)
      const newSearch = cleaned ? `?${cleaned}` : ''
      const newUrl = window.location.pathname + newSearch + window.location.hash
      window.history.replaceState({}, document.title, newUrl)
    }
  }, [prepromptFromUrl])
}

export default useUrlPrompts
